import { AlertTriangle, Globe, Loader2, Pencil, Server } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate } from '../../../shared/format'
import { INFRA_READ_PERM, INFRA_WRITE_PERM, useFootprint, useSetPlacementModules, type Footprint, type FootprintPlacement } from '../../footprint'
import { toastError } from '../../ui'

/**
 * Müşteride neyin nerede çalıştığı: lisanslı modüller hangi sunucu/ortam/adreste, hangi domainlerle.
 * Görüntüleme infra.servers.read (müşteri veri kapsamında), modül eşleme düzenlemesi infra.servers.write ister.
 */
export function FootprintSection({ customerId }: { customerId: string }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canRead = can(INFRA_READ_PERM)
  const canWrite = can(INFRA_WRITE_PERM)
  const q = useFootprint(customerId, canRead)
  const [editing, setEditing] = useState<FootprintPlacement | null>(null)
  if (!canRead) return null

  return (
    <Section title={t('crm.footprint.title')} icon={Server} bodyClassName="p-0">
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.isLoading || !q.data ? (
        <Skeleton className="m-4 h-32" />
      ) : !q.data.modules.length && !q.data.placements.length ? (
        <EmptyState icon={Server} title={t('crm.footprint.empty')} description={t('crm.footprint.emptyHint')} />
      ) : (
        <FootprintBody data={q.data} canWrite={canWrite} onEdit={setEditing} />
      )}
      {editing && q.data ? <ModulesDialog customerId={customerId} placement={editing} modules={q.data.modules} onClose={() => setEditing(null)} /> : null}
    </Section>
  )
}

function FootprintBody({ data, canWrite, onEdit }: { data: Footprint; canWrite: boolean; onEdit: (p: FootprintPlacement) => void }) {
  const { t } = useTranslation()
  const placementLabel = new Map(data.placements.map((p) => [p.id, `${p.serverName || p.hostname} · ${t(`crm.footprint.env.${p.environment}`, { defaultValue: p.environment })}`]))
  return (
    <div className="divide-y">
      {data.modules.length ? (
        <div className="space-y-2 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('crm.footprint.modules')}</p>
          <ul className="space-y-1.5">
            {data.modules.map((m) => (
              <li key={m.code} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{m.name}</span>
                {m.validUntil ? <span className="text-xs text-muted-foreground">{t('crm.footprint.until', { date: fmtDate(m.validUntil) })}</span> : null}
                {m.placementIds.length ? (
                  m.placementIds.map((id) => (
                    <Badge key={id} tone="info">
                      {placementLabel.get(id) ?? id}
                    </Badge>
                  ))
                ) : (
                  <Badge tone="warning">
                    <AlertTriangle className="size-3" aria-hidden />
                    {t('crm.footprint.notPlaced')}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {data.placements.map((p) => (
        <div key={p.id} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
          <div className="min-w-0 flex-1 basis-64 space-y-1">
            <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
              <Server className="size-4 text-muted-foreground" aria-hidden />
              {p.serverName || p.hostname || '—'}
              <Badge tone={p.environment === 'production' ? 'success' : 'neutral'}>{t(`crm.footprint.env.${p.environment}`, { defaultValue: p.environment })}</Badge>
              {p.status !== 'active' ? <Badge tone="warning">{t(`crm.footprint.status.${p.status}`, { defaultValue: p.status })}</Badge> : null}
            </p>
            {p.url ? <p className="truncate font-mono text-xs text-muted-foreground">{p.url}</p> : null}
            <div className="flex flex-wrap gap-1.5">
              {p.moduleCodes.length ? (
                p.moduleCodes.map((c) => (
                  <Badge key={c} tone="primary">
                    {c}
                  </Badge>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">{t('crm.footprint.noModules')}</span>
              )}
            </div>
            {p.domains.length ? (
              <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Globe className="size-3.5" aria-hidden />
                {p.domains.join(', ')}
              </p>
            ) : null}
          </div>
          {canWrite ? (
            <Button size="sm" variant="outline" onClick={() => onEdit(p)}>
              <Pencil />
              {t('crm.footprint.editModules')}
            </Button>
          ) : null}
        </div>
      ))}
      {data.unplacedDomains.length ? (
        <p className="flex flex-wrap items-center gap-1.5 px-4 py-3 text-xs text-muted-foreground">
          <Globe className="size-3.5" aria-hidden />
          {t('crm.footprint.unplacedDomains')}: {data.unplacedDomains.join(', ')}
        </p>
      ) : null}
    </div>
  )
}

/** Yerleşimde çalışan modüller: müşterinin lisanslı modüllerinden seçilir. */
function ModulesDialog({ customerId, placement, modules, onClose }: { customerId: string; placement: FootprintPlacement; modules: Footprint['modules']; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useSetPlacementModules(customerId)
  const [picked, setPicked] = useState(() => new Set(placement.moduleCodes))
  // Lisansı sona ermiş/iptal edilmiş ama yerleşimde kayıtlı kodlar da listede kalır (kaldırılabilsin).
  const options = [...modules.map((m) => ({ code: m.code, name: m.name })), ...placement.moduleCodes.filter((c) => !modules.some((m) => m.code === c)).map((c) => ({ code: c, name: c }))]
  const toggle = (code: string) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })

  return (
    <Modal
      open
      size="md"
      onOpenChange={(v) => !v && onClose()}
      dismissible={!save.isPending}
      icon={<Server />}
      title={t('crm.footprint.editTitle', { server: placement.serverName || placement.hostname })}
      description={t('crm.footprint.editHint')}
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate(
          { placementId: placement.id, codes: [...picked] },
          {
            onSuccess: () => {
              toast.success(t('crm.footprint.saved'))
              onClose()
            },
            onError: toastError,
          },
        )
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      {options.length ? (
        <ul className="divide-y rounded-lg border">
          {options.map((m) => (
            <li key={m.code}>
              <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/50">
                <input type="checkbox" className="size-4 accent-[var(--color-primary)]" checked={picked.has(m.code)} onChange={() => toggle(m.code)} />
                <span className="flex-1">{m.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{m.code}</span>
              </label>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title={t('crm.footprint.noLicensedModules')} />
      )}
    </Modal>
  )
}
